import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { UserContext } from "../../App";
import Select from "react-select";
import { createFolder, deleteFolder, updateFolder } from "../../utils/folder";
import { db } from "../../index";
import { useProviderOptions } from "../../store/actions/providerActions";
import { useTarifsOptions } from "../../utils/estimate-invoice";
import { FooterActionsContainer } from "../layout/Footer";
import { OutlineButton } from "../layout/OutlineButton";

export default function FolderAddOrUpdateContent() {
  const history = useHistory();
  const params = useParams();
  const folderId = params?.folder_id;

  const { userInfos } = useContext(UserContext);

  const options = useProviderOptions();
  const tarifsOptions = useTarifsOptions();

  const [isFetching, setIsFetching] = useState(false);
  const [formError, setFormError] = useState([]);
  const [folder, setFolder] = useState({
    name: "",
    tarif: "",
    provider: {
      id: "",
      name: "",
      phone: "",
      email: "",
      contacts: [],
    },
    contact: {
      name: "",
      phone: "",
      email: "",
      role: "",
    },
  });

  // region Selects
  const contactSelectRef = useRef(null);

  const contactOptions = useMemo(() => {
    if (!folder?.provider?.contacts?.length) return [];
    return folder.provider.contacts.map((contact) => ({
      label: `[${contact.role}] ${contact.name} (${contact.phone}, ${contact.email})`,
      value: contact.email,
      data: contact,
    }));
  }, [folder.provider.contacts]);
  // endregion

  // region Init folder from forlderId
  useEffect(() => {
    if (folderId && userInfos?.uid) {
      db.ref(`users/${userInfos.uid}/folders/${folderId}`)
        .once("value", (snapshot) => {
          const res = snapshot.val();
          setFolder({ ...res, id: snapshot.key });
        })
        .then();
    }
  }, [folderId, userInfos?.uid]);

  // endregion

  function handleChange(e) {
    const { name, value } = e.target;
    setFolder((old) => ({
      ...old,
      [name]: value,
    }));
  }

  async function handleDeleteFolder() {
    await deleteFolder(userInfos, folderId);
    history.push("/folders");
  }

  async function handleFormSubmit(e) {
    e.preventDefault();
    if (handleValidation()) {
      setIsFetching(true);

      if (folderId) {
        await updateFolder(userInfos, folder);
        window.alert("Le dossier a été mis à jour avec succès");
      } else {
        await createFolder(userInfos, folder);
        history.push("/folders");
      }

      setIsFetching(false);
    }
  }

  function handleValidation() {
    let valid = true;
    let message = [];

    if (!folder.name) {
      valid = false;
      message.push("Veuillez renseigner le nom du dossier");
    }

    if (!folder.sharedBy && !folder.tarif) {
      valid = false;
      message.push("Veuillez renseigner le tarif du dossier");
    }

    if (!folder.sharedBy && !folder?.provider?.id) {
      valid = false;
      message.push("Veuillez renseigner la compagnie");
    }

    setFormError(message);

    return valid;
  }

  const FormAlert = useMemo(() => {
    const alert =
      formError.length > 0 ? (
        <div
          className="alert alert-danger"
          role="alert"
          style={{ flexDirection: "column" }}
        >
          {formError.map((error, index) => {
            return (
              <div
                className="alert-text"
                key={index}
                style={{ alignSelf: "flex-start" }}
              >
                {error}
              </div>
            );
          })}
        </div>
      ) : (
        ""
      );

    return (
      <div className="kt-container  kt-container--fluid  kt-grid__item kt-grid__item--fluid">
        {alert}
      </div>
    );
  }, [formError]);

  function handleProviderChange(obj) {
    setFolder((old) => ({
      ...old,
      provider: obj.data,
    }));
    if (contactSelectRef.current?.select)
      contactSelectRef.current.select.clearValue();
  }

  function handleContactChange(obj) {
    setFolder((old) => ({
      ...old,
      contact: obj?.data || {
        name: "",
        phone: "",
        email: "",
        role: "",
      },
    }));
  }

  // region selected and available tarif & product
  const selectedTarif = folder?.tarif
    ? tarifsOptions.find((tarif) => tarif.value === folder.tarif)
    : null;

  const availableProducts =
    selectedTarif && selectedTarif?.data?.products?.length > 3
      ? [
          { label: "Aucun", value: "" },
          ...selectedTarif?.data?.products?.slice(3)?.map((product) => ({
            label: product.label,
            value: product.id,
          })),
        ]
      : null;

  const selectedProduct =
    folder?.tarif_product && availableProducts
      ? availableProducts.find(
          (product) => product.value === folder.tarif_product
        )
      : null;
  // endregion

  const buttonText =
    isFetching === true ? (
      <button type="submit" className="btn btn-primary px-5" disabled>
        {folderId ? "Mise à jour..." : "Création..."}
      </button>
    ) : (
      <button type="submit" className="btn btn-primary px-5">
        {folderId ? "Mettre à jour le dossier" : "Créer le dossier"}
      </button>
    );

  return (
    <div
      className="kt-content  kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
      id="kt_content"
    >
      <div className="kt-subheader   kt-grid__item" id="kt_subheader">
        <div className="kt-container  kt-container--fluid ">
          <div className="kt-subheader__main">
            <h3 className="kt-subheader__title">
              {folderId ? folder?.name : "Créer un dossier"}{" "}
            </h3>
            <div className="kt-subheader__breadcrumbs">
              <Link to="/" className="kt-subheader__breadcrumbs-home">
                <i className="flaticon2-shelter"></i>
              </Link>
              <span className="kt-subheader__breadcrumbs-separator"></span>
              <Link to="/folders/" className="kt-subheader__breadcrumbs-link">
                Dossiers
              </Link>
              {folderId ? (
                <>
                  <span className="kt-subheader__breadcrumbs-separator"></span>
                  <Link
                    to={"/folders/" + folderId}
                    className="kt-subheader__breadcrumbs-link"
                  >
                    {folder.name}
                  </Link>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {FormAlert}

      <div className="kt-container  kt-container--fluid  kt-grid__item kt-grid__item--fluid">
        <div className="kt-portlet kt-portlet--tabs">
          <div className="kt-portlet__head">
            <div className="kt-portlet__head-label">
              <h3 className="kt-portlet__head-title">Données du dossier</h3>
            </div>
          </div>
          <form onSubmit={handleFormSubmit} className="kt-form">
            <div className="kt-portlet__body">
              <div className="kt-section kt-section--first">
                <div className="kt-section__body">
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">
                      Nom du dossier *:
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={folder.name}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">Tarif *:</label>
                    <div className="col-lg-6">
                      <Select
                        options={tarifsOptions}
                        value={selectedTarif}
                        onChange={(obj) =>
                          setFolder((old) => ({ ...old, tarif: obj.value }))
                        }
                      />
                    </div>
                  </div>
                  {availableProducts && availableProducts.length > 0 && (
                    <div className="form-group row">
                      <label className="col-lg-3 col-form-label">
                        Tarif -> Produit :
                      </label>
                      <div className="col-lg-6">
                        <Select
                          options={availableProducts}
                          value={selectedProduct}
                          onChange={(obj) =>
                            setFolder((old) => ({
                              ...old,
                              tarif_product: obj.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  )}
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">Client *:</label>
                    <div className="col-lg-6">
                      <Select
                        options={options}
                        value={
                          folder?.provider?.id
                            ? {
                                label: folder.provider.name,
                                value: folder.provider.id,
                                data: folder.provider,
                              }
                            : null
                        }
                        onChange={handleProviderChange}
                      />
                    </div>
                  </div>
                  {folder?.provider?.id && (
                    <div className="form-group row">
                      <label className="col-lg-3 col-form-label">
                        Contact:
                      </label>
                      <div className="col-lg-6">
                        <Select
                          ref={contactSelectRef}
                          options={contactOptions}
                          value={contactOptions.find(
                            (_c) => _c.value === folder?.contact?.email
                          )}
                          onChange={handleContactChange}
                        />
                      </div>
                    </div>
                  )}
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">
                      Nom du contact:
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="text"
                        className="form-control"
                        value={folder?.contact?.name}
                        disabled
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">
                      Téléphone du contact:
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="text"
                        className="form-control"
                        value={folder?.contact?.phone}
                        disabled
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">
                      Email du contact:
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="text"
                        className="form-control"
                        value={folder?.contact?.email}
                        disabled
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">
                      Fonction du contact:
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="text"
                        className="form-control"
                        value={folder?.contact?.role}
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <FooterActionsContainer>
              <div>
                <OutlineButton
                  onClick={(e) => {
                    e.preventDefault();
                    history.goBack();
                  }}
                >
                  <i className="fas fa-arrow-left"></i>
                  Retour
                </OutlineButton>
              </div>
              <div>{buttonText}</div>
              <div>
                {folderId && (
                  <OutlineButton
                    type="button"
                    data-toggle="modal"
                    data-target="#delete_modal"
                  >
                    <i className="fas fa-trash" />
                    Supprimer
                  </OutlineButton>
                )}
              </div>
            </FooterActionsContainer>
          </form>
        </div>
      </div>
      <div
        className="modal fade"
        id="delete_modal"
        tabIndex="-1"
        role="dialog"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLongTitle">
                Supprimer le dossier
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p>Êtes-vous sûr de vouloir supprimer ce dossier ?</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-dismiss="modal"
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-primary"
                data-dismiss="modal"
                onClick={handleDeleteFolder}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
